import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { cmsGraphql } from '@xolv-mybrightlink/common';
import { useArticleContext } from './useArticleContext';
import { findArticleDetails } from '../reducer/ArticlesReducer';
import { Article, ArticleDetailResponse } from '../models/ArticleResult';
import { useFetchBookmarkedArticles } from './useFetchBookmarkedArticles';

const GET_ARTICLE_DETAILS = gql`
  query PostById($id: ID!, $idType: PostIdType!) {
    post(id: $id, idType: $idType) {
      databaseId
      date
      slug
      title(format: RENDERED)
      databaseId
      categories {
        nodes {
          name
          databaseId
        }
      }
      featuredImage {
        node {
          sourceUrl
        }
      }
      content(format: RENDERED)
      article {
        copyright
        sourceCredit
      }
      tags {
        nodes {
          databaseId
          name
        }
      }
    }
  }
`;

export const useFetchArticleDetails = (databaseIdOrSlug?: string) => {
  const [state, dispatch] = useArticleContext();
  const fetchBookmarkedArticles = useFetchBookmarkedArticles();
  const [data, setData] = useState<'loading' | 'error' | Article>('loading');
  const intl = useIntl();
  const fetch = useCallback(async () => {
    console.log('databaseIdOrSlug', databaseIdOrSlug);
    if (!databaseIdOrSlug || state.initial) {
      return setData('loading');
    } else {
      const parsedNum = Number(databaseIdOrSlug);
      const idType = isNaN(parsedNum) ? 'SLUG' : 'DATABASE_ID';
      const { databaseId, slug } =
        idType === 'SLUG'
          ? { databaseId: undefined, slug: databaseIdOrSlug }
          : { databaseId: parsedNum, slug: undefined };
      const existing = findArticleDetails(
        state.articleDetails,
        databaseId,
        slug,
      );
      if (existing) {
        setData(existing.data);
      } else {
        dispatch({ type: 'article-fetching', databaseId, slug });
        try {
          const result = await cmsGraphql.query<ArticleDetailResponse>({
            query: GET_ARTICLE_DETAILS,
            variables: { id: databaseIdOrSlug, idType },
          });
          const [article] = await fetchBookmarkedArticles([result.data.post]);
          dispatch({ type: 'article-fetched', article });
        } catch {
          toast(intl.formatMessage({ id: 'common.error.message' }), {
            toastId: 'common.error.message',
          });
          dispatch({ type: 'article-error', databaseId, slug });
        }
      }
    }
  }, [databaseIdOrSlug, state, dispatch, intl, fetchBookmarkedArticles]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return data;
};
